import moment from 'moment';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isEqual from 'lodash/isEqual';
import getPath from 'lodash/get';
import reduxStore from '../reduxStore';
import {
  extractSoundcasts,
  soundcastsManagedV2Selector,
  soundcastsManagedEpisodesSelector,
  managedEpisodeByIdSelector,
  episodesCountSelector,
} from '../selectors/episode';
import { authManager, userManager } from './managers';
const {
  getPlanFeatureAvailability,
  getPublisherPlan,
  getPlanFeatureLimits,
} = require('../../server/scripts/utils.js')();

export const getSoundcastsManaged = (userInfo = {}, condition = () => true) => {
  const { soundcasts_managed: soundcastsManaged = {} } = userInfo;
  return extractSoundcasts(soundcastsManaged, condition);
};

export const getSoundcastsManagedv2 = (condition = () => true) => {
  const { managed = {} } = reduxStore.getState();
  const selector = soundcastsManagedV2Selector(condition);
  return selector({ managed });
};

export const getSoundcastManagedEpisodes = soundcastId => {
  const { managed = {} } = reduxStore.getState();
  const selector = soundcastsManagedEpisodesSelector();
  return selector({ managed }, soundcastId);
};

export const getManagedEpisodeById = (episodeId = '') => {
  const { managed = {} } = reduxStore.getState();
  const selector = managedEpisodeByIdSelector();
  return selector({ managed }, episodeId);
};

export const hasManagedEpisodes = () => {
  const { managed = {} } = reduxStore.getState();
  return !isEmpty(managed.episodes.byId);
};

export const getSoundcastsManagedCountV2 = (withBundles = false) => {
  const condition = withBundles ? soundcast => soundcast.bundle : soundcast => !soundcast.bundle;
  const soundcastsManaged = getSoundcastsManagedv2(condition);
  return soundcastsManaged.length;
};

export const getAllEpisodesCountV2 = () => {
  const { managed = {} } = reduxStore.getState();
  const selector = episodesCountSelector();
  return selector({ managed });
};

// TODO: get the highest index from db
// this is to avoid bugs on bulk uploads empty state tree
export const getHighestEpisodeIndex = soundcastId => {
  const managedEpisodes = getSoundcastManagedEpisodes(soundcastId);
  const episodesIndexes = managedEpisodes.map(episode => {
    // if no index is set, use 1
    return episode.index || 1;
  });

  if (isEmpty(episodesIndexes)) {
    return 0;
  }

  return Math.max(...episodesIndexes);
};

export const isSoundcastsManagedLoaded = () => {
  const { managed = {} } = reduxStore.getState();
  const {
    soundcasts: { loaded = false },
  } = managed;

  return loaded;
};

export const isSoundcastsManagedEmpty = () => {
  const { managed = {} } = reduxStore.getState();
  const {
    soundcasts: { empty = false },
  } = managed;

  return empty;
};

export const isSoundcastsManagedLoadedOrEmpty = () => {
  return isSoundcastsManagedLoaded() || isSoundcastsManagedEmpty();
};

export const isSoundcastsManagedLoadedAndNotEmpty = () => {
  return isSoundcastsManagedLoaded() && !isSoundcastsManagedEmpty();
};

export const isSoundcastsManagedLoadedAndCompleted = () => {
  return isSoundcastsManagedLoaded() && isSoundcastsManagedComplete();
};

export const isSoundcastsManagedBusy = () => {
  const { managed = {} } = reduxStore.getState();
  const { soundcasts } = managed;
  return soundcasts.busy;
};

export const isSoundcastsManagedComplete = () => {
  const { managed = {} } = reduxStore.getState();
  const { soundcasts } = managed;
  return soundcasts.complete;
};

export const isSoundcastManagedEpisodesBusy = (soundcastId, managed) => {
  const { busy } = managed.episodes;
  return getPath(busy, soundcastId, false);
};

export const identifyManagedOperation = managed => {
  const { operation = '' } = managed.episodes;
  return operation;
};

export const isUserPlanAlive = userInfo => {
  if (isEmpty(userInfo) || !isObject(userInfo)) {
    return false;
  }

  if (!('publisher' in userInfo)) {
    return false;
  }

  // if free user just return its active
  const { publisher } = userInfo;
  if (!('plan' in publisher) && !('current_period_end' in publisher)) {
    return true;
  }

  const { current_period_end: expiration = 0 } = publisher;
  const now = moment().unix();
  return expiration > now;
};

export const getUserProStatus = userInfo => {
  let proUser = false;
  let userPlan = '';
  if (!isEmpty(userInfo) && 'publisher' in userInfo && isObject(userInfo.publisher)) {
    if ('plan' in userInfo.publisher) {
      const inSeconds = Number(moment().format('X'));
      proUser = userInfo.publisher.current_period_end > inSeconds;
      if (proUser) {
        userPlan = userInfo.publisher.plan;
      }
    } else if ('beta' in userInfo.publisher) {
      proUser = true;
    }
  }

  return {
    proUser,
    userPlan,
  };
};

export const getUsageCurrent = type => {
  const { usage = {} } = reduxStore.getState();
  const { current = {} } = usage;
  return type in current ? current[type] : null;
};

export const getUsageLimit = type => {
  const { usage = {} } = reduxStore.getState();
  const { limits = {} } = usage;
  return type in limits ? limits[type] : null;
};

export const canUserAccessFeature = feature => {
  const { usage } = reduxStore.getState();
  const { features = [] } = usage;
  if (isEmpty(features)) {
    return false;
  }

  return features.includes(feature);
};

export const canUserAccessFeatures = features => {
  return features.every(feature => canUserAccessFeature(feature));
};

export const getFeatureAvailability = (feature, concat = true) => {
  const { user } = reduxStore.getState();
  const publisher = getPath(user, 'userInfo.publisher', {});
  const availableToPlans = getPlanFeatureAvailability(publisher, feature);
  if (!concat) return availableToPlans;
  if (isEmpty(availableToPlans)) return '';
  if (availableToPlans.length === 1) return `${availableToPlans[0]}`;
  const lastPlan = availableToPlans.pop();
  return `${availableToPlans.join(', ').toUpperCase()} and ${lastPlan.toUpperCase()}`;
};

export const canUserUse100PercenOffCoupon = (prices = []) => {
  if (isEmpty(prices)) {
    return true;
  }

  const with100PercentOff = coupon =>
    coupon.couponType === 'discount' && coupon.percentOff === '100';

  const has100PercentOff = prices
    .map(price => ('coupons' in price ? price.coupons : []))
    .filter(coupons => !isEmpty(coupons))
    .some(coupons => {
      const found = coupons.find(coupon => with100PercentOff(coupon));
      return !isEmpty(found) ? true : false;
    });

  if (!has100PercentOff) {
    return true;
  }

  // ensure the feature is allowed
  if (!canUserAccessFeature('SOUNDCAST_COUPONS_100POFF')) {
    return false;
  }

  return true;
};

export const hasUserExceededContentPlanLimit = (soundcastsCount = 0, episodesCount = 0) => {
  const { user } = reduxStore.getState();
  const publisher = getPath(user, 'userInfo.publisher', {});
  const allowedSoundcastsCount = getUsageLimit('ALLOWED_SOUNDCASTS');
  const allowedEpisodesCount = getUsageLimit('ALLOWED_EPISODES');
  const soundcastsManagedCount = getSoundcastsManagedCountV2();
  const allEpisodesCount = getAllEpisodesCountV2();

  // always exceed if plan is free and has soundcast and episode
  const { active } = getPublisherPlan(publisher);
  if (!active) {
    if (
      soundcastsManagedCount >= allowedSoundcastsCount &&
      allEpisodesCount >= allowedEpisodesCount
    ) {
      return true;
    }
  }

  // from here it should be a paid plan
  // if plan is less than the required soundcast, then episode is always unlimited
  soundcastsCount = soundcastsCount > 0 ? soundcastsCount : soundcastsManagedCount;
  if (soundcastsCount <= allowedSoundcastsCount) {
    return false;
  }

  // if soundcast is more than the allowed soundcast
  // then episodes should be less than the allowed episodes
  episodesCount = episodesCount > 0 ? episodesCount : allEpisodesCount;
  if (episodesCount > allowedEpisodesCount) {
    return true;
  }

  return false;
};

export const hasUserExceededPodcastFeedLimit = () => {
  const currentPodcastFeeds = getUsageCurrent('podcast_feeds');
  const allowedPodcastFeeds = getUsageLimit('ALLOWED_PODCAST_FEEDS');
  return currentPodcastFeeds >= allowedPodcastFeeds;
};

export const hasUserExceededAdminLimit = () => {
  const currentAdmins = getUsageCurrent('admins');
  const allowedAdmins = getUsageLimit('ALLOWED_ADMINS');
  return currentAdmins >= allowedAdmins;
};

export const getUserPlatformCustomerId = (email = '') => {
  return new Promise(async resolve => {
    try {
      if (isEmpty(email)) {
        throw new Error('Email provided is empty');
      }

      // check if this email has an account with us
      const provider = await authManager.fetchSignInMethodsForEmail(email);
      if (isEmpty(provider) || !isEqual(provider[0], 'password')) {
        throw new Error('Email does not exist');
      }

      // retrieve the userId by email
      const { status, data } = await axios.get(`/api/user/lookup/${email}`);
      const { uid: userId, email: vemail } = data;
      if (isEqual(status, 200) && isEqual(email, vemail)) {
        // pull details from firebase itself
        const stripeId = await userManager.getProp(userId, 'stripe_id', '');
        if (isEmpty(stripeId)) {
          throw new Error('Customer Id for user does not exist.');
        }

        return resolve(stripeId);
      }

      throw new Error('Invalid status request');
    } catch (err) {
      console.log('Unable to pull user platform customer id', email, err.message);
      resolve('');
    }
  });
};

export const updateUserEmail = async (userId, email) => {
  await axios.patch(`/api/user/${userId}/email`, { email }).catch(error => {
    if (error.response) {
      if (error.response.status && error.response.status === 400) {
        throw new Error();
      }
      if (error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      }
    }
  });
};

export const getUserIdByEmail = (email = '') => {
  return new Promise(async resolve => {
    try {
      if (isEmpty(email)) {
        throw new Error('Email provided is empty');
      }

      // check if this email has an account with us
      const provider = await authManager.fetchSignInMethodsForEmail(email);
      if (isEmpty(provider) || !isEqual(provider[0], 'password')) {
        throw new Error('Email does not exist');
      }

      // retrieve the userId by email
      const { status, data } = await axios.get(`/api/user/lookup/${email}`);
      const { uid: userId = '', email: vemail } = data;
      if (isEqual(status, 200) && isEqual(email.toLowerCase(), vemail.toLowerCase())) {
        return resolve(userId);
      }

      throw new Error('Invalid status request');
    } catch (err) {
      console.log('Unable to pull user id', email, err.message);
      resolve('');
    }
  });
};

import { publisherManager } from './managers';
const { isMonitoringAccount } = require('../../server/scripts/utils.js')();

export const checkMonitoringUserSoundcastsManaged = async (userInfo) => {
  try {
    const {
      admin = false, id, publisherID = '', email = [],
      soundcasts_managed: soundcastsManaged = {},
    } = userInfo || {};
  
    if (!admin || isEmpty(id) || isEmpty(publisherID) || isEmpty(email) || !isMonitoringAccount(userInfo)) {
      return;
    }
  
    const publisherSoundcasts = await publisherManager.getProp(publisherID, 'soundcasts', {});
    if (isEqual(soundcastsManaged, publisherSoundcasts)) {
      return;
    }
    await userManager.saveProp(id, 'soundcasts_managed', publisherSoundcasts);
  } catch (err) {
    console.warn(`WARNING: checkMonitoringUserSoundcastsManaged errored`, err);
  }
};
